// Dev-only on-screen debug overlay for diagnosing mobile scroll bugs.
// Gated by NODE_ENV !== 'production' OR a `?debug=1` URL param, so it
// never ships to the live site unless explicitly opted in.

const MAX_LOG_LINES = 24;

const shouldEnable = () => {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV !== 'production') return true;
  return new URLSearchParams(window.location.search).has('debug');
};

const buffer = [];
let panel = null;

const fmtTime = () => (performance.now() / 1000).toFixed(2) + 's';

const push = (line) => {
  buffer.push(`${fmtTime()} ${line}`);
  if (buffer.length > MAX_LOG_LINES) buffer.shift();
  if (panel) panel.textContent = buffer.join('\n');
};

const createPanel = () => {
  panel = document.createElement('div');
  Object.assign(panel.style, {
    position: 'fixed',
    bottom: '0',
    right: '0',
    width: '60vw',
    maxHeight: '45vh',
    overflow: 'hidden',
    background: 'rgba(0,0,0,0.75)',
    color: '#0f0',
    font: '10px/1.3 monospace',
    whiteSpace: 'pre-wrap',
    padding: '4px 6px',
    zIndex: '99999',
    pointerEvents: 'none',
  });
  document.body.appendChild(panel);
};

const installEventLoggers = () => {
  const touchHandler = (e) => {
    const tag = e.target && e.target.tagName ? e.target.tagName.toLowerCase() : '?';
    push(`${e.type} on <${tag}>`);
  };
  window.addEventListener('touchstart', touchHandler, { passive: true, capture: true });
  window.addEventListener('touchend', touchHandler, { passive: true, capture: true });
};

const observeShiftedClass = () => {
  const banner = document.getElementById('home');
  if (!banner) {
    push('WARN: #home not found');
    return;
  }
  let last = banner.classList.contains('shifted');
  const obs = new MutationObserver(() => {
    const now = banner.classList.contains('shifted');
    if (now !== last) {
      push(`.shifted -> ${now}`);
      last = now;
    }
  });
  obs.observe(banner, { attributes: true, attributeFilter: ['class'] });
};

// Mirror console.log lines to the overlay. This captures the existing
// scrollToSection debug output (scrollConfig.js) without requiring
// any changes to that module.
const mirrorConsoleLog = () => {
  const orig = console.log.bind(console);
  console.log = (...args) => {
    try {
      const line = args
        .map((a) => (typeof a === 'string' ? a : JSON.stringify(a)))
        .join(' ');
      // Only mirror log lines relevant to scroll diagnostics, to keep the
      // overlay readable. Widen this filter if other signals are needed.
      if (/scroll|navbar|elementRect|targetPosition|collapsed/i.test(line)) {
        push(line);
      }
    } catch (err) {
      // swallow — never let the overlay break logging
    }
    orig(...args);
  };
};

export const initMobileDebugOverlay = () => {
  if (!shouldEnable()) return;
  const boot = () => {
    try {
      createPanel();
      installEventLoggers();
      observeShiftedClass();
      mirrorConsoleLog();
      push('mobileDebugOverlay ready');
    } catch (err) {
      // Never let the overlay break the app
      // eslint-disable-next-line no-console
      console.error('mobileDebugOverlay boot error', err);
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
};
